import { usePublicShare, usePublicShareSpecimen, usePublicShareSpecimens } from '@numistic/data'
import { useTranslation } from '@numistic/i18n'
import { Alert, Button, Card, CardContent, Input } from '@numistic/ui'
import { createRoute, Link, useNavigate } from '@tanstack/react-router'
import { useCallback } from 'react'
import { z } from 'zod'

import { ImageGallery } from '../components/image-gallery.js'
import { PageLoading } from '../components/page-loading.js'
import { useUrlSearchText } from '../lib/url-search-text.js'
import { TreeView } from '../collections/tree-view.js'
import { useErrorText } from '../lib/errors.js'
import { MetaView } from '../meta/meta-view.js'
import { rootRoute } from './root.js'

/** Коллекция по ссылке «только чтение» — без входа, без цен и истории. */
export const shareRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/share/$token',
  validateSearch: z.object({
    c: z.string().optional().catch(undefined),
    q: z.coerce.string().optional().catch(undefined),
  }),
  component: SharePage,
})

export const shareSpecimenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/share/$token/s/$id',
  component: SharedSpecimenPage,
})

function SharePage() {
  const { t } = useTranslation()
  const errorText = useErrorText()
  const { token } = shareRoute.useParams()
  const search = shareRoute.useSearch()
  const navigate = useNavigate({ from: '/share/$token' })
  const collection = usePublicShare(token)
  const setQ = useCallback(
    (q: string | undefined) => void navigate({ search: (s) => ({ ...s, q }), replace: true }),
    [navigate],
  )
  const [text, setText] = useUrlSearchText(search.q, setQ)
  const list = usePublicShareSpecimens(token, { collectionId: search.c, q: search.q })
  const items = list.data?.pages.flatMap((p) => p.items) ?? []

  if (collection.error) return <Alert variant="destructive">{t('share.unavailable')}</Alert>
  const data = collection.data
  if (!data) return <PageLoading />
  const hasChildren = data.tree.length > 1

  return (
    <div className="grid gap-6">
      <div className="grid gap-1">
        <h1 className="text-2xl font-semibold">{data.name}</h1>
        <p className="text-sm text-muted-foreground">
          {t('share.ownerLine', { name: data.ownerName })}
        </p>
        {data.description ? <p className="text-sm">{data.description}</p> : null}
      </div>
      <div className={hasChildren ? 'grid gap-6 lg:grid-cols-[16rem_1fr]' : 'grid gap-6'}>
        {hasChildren ? (
          <aside>
            <TreeView
              nodes={data.tree}
              rootLabel={t('share.all')}
              selectedId={search.c ?? null}
              onSelect={(id) => navigate({ search: (s) => ({ ...s, c: id ?? undefined }) })}
            />
          </aside>
        ) : null}
        <section className="grid content-start gap-4">
          <Input
            type="search"
            aria-label={t('common.search')}
            placeholder={t('catalog.searchPlaceholder')}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          {list.error ? <Alert variant="destructive">{errorText.request(list.error)}</Alert> : null}
          {list.data && !items.length ? (
            <p className="text-sm text-muted-foreground">{t('collections.empty')}</p>
          ) : null}
          <ul
            className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4"
            aria-label={data.name}
          >
            {items.map((item) => (
              <li key={item.id} className="rounded-lg border bg-card p-2">
                <Link
                  to="/share/$token/s/$id"
                  params={{ token, id: item.id }}
                  className="group grid gap-2"
                >
                  <div className="flex aspect-square items-center justify-center overflow-hidden rounded-md bg-muted">
                    {item.thumb ? (
                      <img
                        src={item.thumb}
                        alt=""
                        loading="lazy"
                        className="size-full object-contain"
                      />
                    ) : null}
                  </div>
                  <span className="line-clamp-2 px-1 pb-1 text-sm font-medium group-hover:text-primary">
                    {item.title}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
          {list.hasNextPage ? (
            <Button
              variant="outline"
              className="justify-self-center"
              disabled={list.isFetchingNextPage}
              onClick={() => void list.fetchNextPage()}
            >
              {t('catalog.more')}
            </Button>
          ) : null}
        </section>
      </div>
    </div>
  )
}

function SharedSpecimenPage() {
  const { t } = useTranslation()
  const { token, id } = shareSpecimenRoute.useParams()
  const specimen = usePublicShareSpecimen(token, id)
  if (specimen.error) return <Alert variant="destructive">{t('share.unavailable')}</Alert>
  const data = specimen.data
  if (!data) return null
  return (
    <article className="grid gap-6">
      <div className="grid gap-1">
        <Link
          to="/share/$token"
          params={{ token }}
          search={{}}
          className="text-sm text-muted-foreground hover:text-foreground"
        >
          ← {t('share.back')}
        </Link>
        <h1 className="text-2xl font-semibold">{data.title}</h1>
      </div>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,24rem)_1fr]">
        <ImageGallery images={data.images} title={data.title} />
        <Card>
          <CardContent className="pt-6">
            <MetaView
              typeCode={data.typeCode}
              level="specimen"
              values={data.values}
              hide={['title']}
              grouped
              sectionOrder={['specimen', 'main', 'physical', 'paper', 'design', 'notes']}
            />
          </CardContent>
        </Card>
      </div>
    </article>
  )
}
